import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ArrowLeft, Check, Upload, Loader2, AlertCircle } from "lucide-react";
import {
  INTENTION_OPTIONS, COMMON_STEPS,
  RACHAT_STEPS, PASSIF_STEPS, DELEGATION_STEPS, INCERTAIN_STEPS,
  routeIncertain, RESULT_MESSAGES
} from "./formConfig";
import { submitDiagnostic, uploadFile } from "./ghlMapping";

const STORAGE_KEY = "cledici_diagnostic_draft";

// -- Field Components --

const TextField = ({ field, value, onChange, error }) => (
  <div className={field.half ? "w-full" : "w-full"}>
    <label className="block text-sm text-[#A1A1AA] mb-1.5">{field.label}{field.required && <span className="text-[#FF4500]"> *</span>}</label>
    <input
      type={field.type === "email" ? "email" : field.type === "tel" ? "tel" : field.type === "number" ? "number" : "text"}
      value={value || ""}
      onChange={e => onChange(field.id, e.target.value)}
      placeholder={field.placeholder}
      className={`w-full bg-[#111111] border ${error ? "border-red-500" : "border-white/10"} rounded-lg px-4 py-3 text-white placeholder:text-[#52525B] focus:outline-none focus:border-[#FF4500]/50 transition-colors`}
      data-testid={`field-${field.id}`}
    />
    {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
  </div>
);

const SelectField = ({ field, value, onChange }) => (
  <div className="w-full">
    <label className="block text-sm text-[#A1A1AA] mb-1.5">{field.label}{field.required && <span className="text-[#FF4500]"> *</span>}</label>
    <select
      value={value || ""}
      onChange={e => onChange(field.id, e.target.value)}
      className="w-full bg-[#111111] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#FF4500]/50 transition-colors appearance-none"
      data-testid={`field-${field.id}`}
    >
      <option value="" className="text-[#52525B]">Sélectionner...</option>
      {field.options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  </div>
);

const SelectCards = ({ field, value, onChange }) => (
  <div className="w-full">
    <label className="block text-sm text-[#A1A1AA] mb-3">{field.label}{field.required && <span className="text-[#FF4500]"> *</span>}</label>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {field.options.map(opt => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(field.id, opt.value)}
          className={`text-left px-4 py-3 rounded-lg border transition-all text-sm ${
            value === opt.value
              ? "border-[#FF4500] bg-[#FF4500]/10 text-white"
              : "border-white/10 bg-[#111111] text-[#A1A1AA] hover:border-white/20"
          }`}
          data-testid={`card-${field.id}-${opt.value}`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  </div>
);

const MultiSelect = ({ field, value, onChange }) => {
  const selected = Array.isArray(value) ? value : [];
  const toggle = (val) => {
    const next = selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val];
    onChange(field.id, next);
  };
  return (
    <div className="w-full">
      <label className="block text-sm text-[#A1A1AA] mb-3">{field.label}{field.required && <span className="text-[#FF4500]"> *</span>}</label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {field.options.map(opt => (
          <button
            key={opt.value}
            type="button"
            onClick={() => toggle(opt.value)}
            className={`text-left px-4 py-3 rounded-lg border transition-all text-sm flex items-center gap-2 ${
              selected.includes(opt.value)
                ? "border-[#FF4500] bg-[#FF4500]/10 text-white"
                : "border-white/10 bg-[#111111] text-[#A1A1AA] hover:border-white/20"
            }`}
            data-testid={`multi-${field.id}-${opt.value}`}
          >
            <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
              selected.includes(opt.value) ? "bg-[#FF4500] border-[#FF4500]" : "border-white/20"
            }`}>
              {selected.includes(opt.value) && <Check className="w-3 h-3 text-white" />}
            </div>
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
};

const SliderField = ({ field, value, onChange }) => {
  const val = value || 5;
  return (
    <div className="w-full">
      <label className="block text-sm text-[#A1A1AA] mb-3">{field.label}{field.required && <span className="text-[#FF4500]"> *</span>}</label>
      <div className="flex items-center gap-4">
        <span className="text-sm text-[#52525B] w-6">{field.min}</span>
        <input
          type="range"
          min={field.min}
          max={field.max}
          value={val}
          onChange={e => onChange(field.id, parseInt(e.target.value))}
          className="flex-1 accent-[#FF4500] h-2 bg-[#111111] rounded-lg"
          data-testid={`slider-${field.id}`}
        />
        <span className="text-sm text-[#52525B] w-6">{field.max}</span>
      </div>
      <div className="text-center mt-2">
        <span className={`text-2xl font-bold ${val >= 7 ? "text-red-400" : val >= 4 ? "text-yellow-400" : "text-green-400"}`}>{val}</span>
        <span className="text-xs text-[#52525B] ml-2">/10</span>
      </div>
    </div>
  );
};

const TextareaField = ({ field, value, onChange }) => (
  <div className="w-full">
    <label className="block text-sm text-[#A1A1AA] mb-1.5">{field.label}</label>
    <textarea
      value={value || ""}
      onChange={e => onChange(field.id, e.target.value)}
      placeholder={field.placeholder}
      rows={3}
      className="w-full bg-[#111111] border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-[#52525B] focus:outline-none focus:border-[#FF4500]/50 transition-colors resize-none"
      data-testid={`field-${field.id}`}
    />
  </div>
);

const FileUploadField = ({ field, value, onChange, formData, setFormData }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const files = formData.uploaded_files || [];

  const handleUpload = async (e) => {
    const newFiles = Array.from(e.target.files);
    setUploading(true);
    setError(null);
    try {
      const results = [];
      for (const f of newFiles) {
        const result = await uploadFile(f);
        results.push(result);
      }
      setFormData(prev => ({ ...prev, uploaded_files: [...(prev.uploaded_files || []), ...results] }));
    } catch (err) {
      setError("Erreur lors de l'upload. Réessayez.");
    }
    setUploading(false);
  };

  return (
    <div className="w-full">
      <label className="block text-sm text-[#A1A1AA] mb-2">{field.label}</label>
      <label className="flex items-center justify-center gap-2 px-4 py-4 border-2 border-dashed border-white/10 rounded-lg cursor-pointer hover:border-[#FF4500]/30 transition-colors" data-testid={`upload-${field.id}`}>
        {uploading ? <Loader2 className="w-5 h-5 text-[#FF4500] animate-spin" /> : <Upload className="w-5 h-5 text-[#52525B]" />}
        <span className="text-sm text-[#71717A]">{uploading ? "Upload en cours..." : "Cliquer pour ajouter des fichiers"}</span>
        <input type="file" className="hidden" accept={field.accept} multiple={field.multiple} onChange={handleUpload} disabled={uploading} />
      </label>
      {error && <p className="text-red-400 text-xs mt-1"><AlertCircle className="inline w-3 h-3 mr-1" />{error}</p>}
      {files.length > 0 && (
        <div className="mt-2 space-y-1">
          {files.map((f, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-[#A1A1AA] bg-[#111111] rounded px-3 py-2">
              <Check className="w-3 h-3 text-green-400" />
              <span className="truncate">{f.filename}</span>
              <button type="button" onClick={() => setFormData(prev => ({ ...prev, uploaded_files: prev.uploaded_files.filter((_, j) => j !== i) }))} className="ml-auto text-[#52525B] hover:text-red-400">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// -- Render a field based on type --
const RenderField = ({ field, formData, setFormData, errors }) => {
  // Conditional visibility
  if (field.showIf && formData[field.showIf.field] !== field.showIf.value) return null;

  const onChange = (id, val) => setFormData(prev => ({ ...prev, [id]: val }));
  const val = formData[field.id];

  switch (field.type) {
    case "text": case "email": case "tel": case "number":
      return <TextField field={field} value={val} onChange={onChange} error={errors[field.id]} />;
    case "select":
      return <SelectField field={field} value={val} onChange={onChange} />;
    case "select-cards":
      return <SelectCards field={field} value={val} onChange={onChange} />;
    case "multi":
      return <MultiSelect field={field} value={val} onChange={onChange} />;
    case "slider":
      return <SliderField field={field} value={val} onChange={onChange} />;
    case "textarea":
      return <TextareaField field={field} value={val} onChange={onChange} />;
    case "file":
      return <FileUploadField field={field} value={val} onChange={onChange} formData={formData} setFormData={setFormData} />;
    default:
      return null;
  }
};

// -- Main Form Component --
const DiagnosticFormNew = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [tunnel, setTunnel] = useState(null); // vendre | passif | deleguer
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  // Build steps dynamically based on tunnel
  const getSteps = useCallback(() => {
    const steps = [{ id: "intention", title: "Votre situation", subtitle: "Quel est votre objectif principal ?" }];
    steps.push(...COMMON_STEPS);

    if (formData.intention === "incertain" && !tunnel) {
      steps.push(...INCERTAIN_STEPS);
    } else {
      const t = tunnel || formData.intention;
      if (t === "vendre") steps.push(...RACHAT_STEPS);
      else if (t === "passif") steps.push(...PASSIF_STEPS);
      else if (t === "deleguer") steps.push(...DELEGATION_STEPS);
    }

    steps.push({ id: "result", title: "Terminé" });
    return steps;
  }, [formData.intention, tunnel]);

  const steps = getSteps();
  const step = steps[currentStep];
  const totalSteps = steps.length;
  const progress = ((currentStep) / (totalSteps - 1)) * 100;
  const isResultStep = step?.id === "result";
  const isIntentionStep = step?.id === "intention";

  // localStorage persistence
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setFormData(parsed.formData || {});
        setTunnel(parsed.tunnel || null);
      } catch {}
    }
  }, []);

  useEffect(() => {
    if (Object.keys(formData).length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ formData, tunnel }));
    }
  }, [formData, tunnel]);

  const clearDraft = () => {
    localStorage.removeItem(STORAGE_KEY);
    setFormData({});
    setCurrentStep(0);
    setTunnel(null);
    setSubmitted(false);
    setErrors({});
  };

  // Validation
  const validateStep = () => {
    if (isIntentionStep) return !!formData.intention;
    if (isResultStep) return true;
    const errs = {};
    for (const field of (step.fields || [])) {
      if (field.showIf && formData[field.showIf.field] !== field.showIf.value) continue;
      if (field.required) {
        const val = formData[field.id];
        if (!val || (Array.isArray(val) && val.length === 0)) {
          errs[field.id] = "Ce champ est requis";
        }
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Navigation
  const next = async () => {
    if (!validateStep()) return;

    // After uncertain tunnel questions, auto-route
    if (step.id === "incertain-1") {
      const routed = routeIncertain(formData);
      setTunnel(routed);
      setCurrentStep(prev => prev + 1);
      return;
    }

    // If this is the last step before result, submit
    if (currentStep === totalSteps - 2) {
      setSubmitting(true);
      try {
        const finalTunnel = tunnel || formData.intention;
        await submitDiagnostic(formData, finalTunnel);
        setSubmitted(true);
        clearDraftStorage();
      } catch (err) {
        console.error("Submit error:", err);
      }
      setSubmitting(false);
      setCurrentStep(prev => prev + 1);
      return;
    }

    setCurrentStep(prev => prev + 1);
  };

  const prev = () => {
    if (currentStep > 0) {
      setErrors({});
      setCurrentStep(prev => prev - 1);
    }
  };

  const clearDraftStorage = () => localStorage.removeItem(STORAGE_KEY);

  const handleClose = () => {
    if (submitted) clearDraft();
    onClose();
  };

  if (!isOpen) return null;

  const finalTunnel = tunnel || formData.intention;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={handleClose}
        data-testid="diagnostic-modal-overlay"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.3 }}
          className="bg-[#0A0A0A] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto relative"
          onClick={e => e.stopPropagation()}
          data-testid="diagnostic-modal"
        >
          {/* Header */}
          <div className="sticky top-0 z-10 bg-[#0A0A0A] border-b border-white/5 px-6 py-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-md bg-[#FF4500] flex items-center justify-center">
                  <span className="font-bold text-white text-xs">CE</span>
                </div>
                <span className="text-sm font-medium text-white">Diagnostic</span>
              </div>
              <button onClick={handleClose} className="text-[#52525B] hover:text-white transition-colors" data-testid="diagnostic-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            {!isResultStep && (
              <div className="w-full bg-[#1A1A1A] rounded-full h-1.5">
                <motion.div
                  className="bg-[#FF4500] h-1.5 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.3 }}
                  data-testid="progress-bar"
                />
              </div>
            )}
          </div>

          {/* Content */}
          <div className="px-6 py-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={step.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.25 }}
              >
                {/* Intention Step */}
                {isIntentionStep && (
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2" data-testid="step-title">Quelle est votre situation aujourd'hui ?</h2>
                    <p className="text-[#71717A] text-sm mb-6">Sélectionnez l'option qui vous correspond le mieux.</p>
                    <div className="space-y-3">
                      {INTENTION_OPTIONS.map(opt => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => {
                            setFormData(prev => ({ ...prev, intention: opt.value }));
                            setTunnel(null);
                            setErrors({});
                          }}
                          className={`w-full text-left px-5 py-4 rounded-xl border transition-all ${
                            formData.intention === opt.value
                              ? "border-[#FF4500] bg-[#FF4500]/10"
                              : "border-white/10 bg-[#111111] hover:border-white/20"
                          }`}
                          data-testid={`intention-${opt.value}`}
                        >
                          <span className={`font-medium ${formData.intention === opt.value ? "text-white" : "text-[#A1A1AA]"}`}>{opt.label}</span>
                          <p className="text-xs text-[#52525B] mt-0.5">{opt.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Result Step */}
                {isResultStep && (
                  <div className="text-center py-8" data-testid="result-screen">
                    <div className="w-16 h-16 rounded-full bg-[#FF4500]/10 flex items-center justify-center mx-auto mb-6">
                      <Check className="w-8 h-8 text-[#FF4500]" />
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-3">{RESULT_MESSAGES[finalTunnel]?.title || "Merci."}</h2>
                    <p className="text-[#A1A1AA] mb-4">{RESULT_MESSAGES[finalTunnel]?.subtitle}</p>
                    <p className="text-[#71717A] text-sm mb-8">{RESULT_MESSAGES[finalTunnel]?.body}</p>
                    <p className="text-[#FF4500] font-medium text-sm">{RESULT_MESSAGES[finalTunnel]?.cta}</p>
                  </div>
                )}

                {/* Form Steps */}
                {!isIntentionStep && !isResultStep && (
                  <div>
                    <h2 className="text-xl font-bold text-white mb-1" data-testid="step-title">{step.title}</h2>
                    <p className="text-[#71717A] text-sm mb-6">{step.subtitle}</p>
                    <div className="space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {(step.fields || []).map(field => {
                          const isHalf = field.half && (field.type === "text" || field.type === "email" || field.type === "tel" || field.type === "number" || field.type === "select");
                          return (
                            <div key={field.id} className={isHalf ? "" : "sm:col-span-2"}>
                              <RenderField field={field} formData={formData} setFormData={setFormData} errors={errors} />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Footer Navigation */}
          {!isResultStep && (
            <div className="sticky bottom-0 bg-[#0A0A0A] border-t border-white/5 px-6 py-4 flex items-center justify-between">
              <div>
                {currentStep > 0 && (
                  <button onClick={prev} className="flex items-center gap-1.5 text-sm text-[#71717A] hover:text-white transition-colors" data-testid="btn-prev">
                    <ArrowLeft className="w-4 h-4" /> Retour
                  </button>
                )}
              </div>
              <button
                onClick={next}
                disabled={submitting || (isIntentionStep && !formData.intention)}
                className="flex items-center gap-2 bg-[#FF4500] hover:bg-[#E03C00] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold px-6 py-3 rounded-lg text-sm transition-all"
                data-testid="btn-next"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Envoi en cours...</>
                ) : currentStep === totalSteps - 2 ? (
                  <><Check className="w-4 h-4" /> {formData.intention === "vendre" ? "Faire étudier mon dossier" : "Recevoir mon analyse"}</>
                ) : (
                  <>Continuer <ArrowRight className="w-4 h-4" /></>
                )}
              </button>
            </div>
          )}

          {/* Result close button */}
          {isResultStep && (
            <div className="px-6 pb-6">
              <button onClick={handleClose} className="w-full py-3 bg-[#111111] border border-white/10 rounded-lg text-white text-sm hover:border-[#FF4500]/30 transition-colors" data-testid="btn-close-result">
                Fermer
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DiagnosticFormNew;
